import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { ComponentProps, forwardRef, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Callout, InputBox } from '../../../../../components';
import { CostNegotiationMode } from '../../../../IPA/Negociacoes/pages/NegociacaoFornecedor/costNegotiation';
import Layout from '../Layout';
import defaultStyles from '../SetupIpaProductsSegmentation/SetupIpaProductsSegmentation.module.scss';
import { useSupplierNegotiationConfig } from './hooks/useSupplierNegotiationConfig';
import styles from './SetupSupplierNegotiationConfig.module.scss';
import { SupplierNegotiationConfigSchema, supplierNegotiationConfigSchema } from './SetupSupplierNegotiationConfig.schema';

const { Section, Footer, Subtitle, Title, Option, Action } = Layout;

export type SetupSupplierNegotiationConfigProps = Omit<ComponentProps<'form'>, 'children'>;

const ENTRY_COST_WARNING =
  'Ao habilitar o uso do custo de entrada, é fundamental que a integração de dados receba os dados de custo de entrada e taxas de impostos, ' +
  'garantindo a sincronização dos dados com o ERP.';

const SetupSupplierNegotiationConfig = forwardRef<HTMLFormElement, SetupSupplierNegotiationConfigProps>(({ className, ...props }, ref) => {
  const { data, saveSupplierNegotiationConfig } = useSupplierNegotiationConfig();

  const form = useForm<SupplierNegotiationConfigSchema>({
    resolver: zodResolver(supplierNegotiationConfigSchema),
    defaultValues: {
      negotiationCost: CostNegotiationMode.NET_COST,
    },
  });

  const selectedCostNegotiation = form.watch('negotiationCost');

  const handleSubmit = useCallback(
    async (formData: SupplierNegotiationConfigSchema) => {
      await saveSupplierNegotiationConfig(formData);
    },
    [saveSupplierNegotiationConfig],
  );

  const handleReset = useCallback(() => {
    form.reset({
      negotiationCost: data?.negotiation_cost ?? CostNegotiationMode.NET_COST,
    });
  }, [data, form]);

  useEffect(() => {
    form.reset({
      negotiationCost: data?.negotiation_cost ?? CostNegotiationMode.NET_COST,
    });
  }, [data, form]);

  return (
    <Layout.Root {...props} ref={ref} className={classNames(styles.wrapper, className)} onSubmit={form.handleSubmit(handleSubmit)} onReset={handleReset}>
      <Section.Root className={styles.mainSection}>
        <Section.Header>
          <Title>Negociação com fornecedores</Title>
          <Subtitle>Defina as configurações para o módulo de negociação com fornecedores</Subtitle>
        </Section.Header>

        <div className={styles.divider} />

        <Section.Content className={styles.sectionContent}>
          <Option.Root className={styles.costOption}>
            <Option.Header>
              <Option.Title>CUSTO PARA NEGOCIAÇÃO</Option.Title>
              <Option.Subtitle>Defina qual custo considerar no módulo de negociação</Option.Subtitle>
            </Option.Header>
            <Option.Content className={classNames(defaultStyles['option__content'], styles.optionContent)}>
              <Controller
                control={form.control}
                name="negotiationCost"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <InputBox>
                    <InputBox.RadioGroup className={styles.radioGroup} value={value} onChange={onChange}>
                      <InputBox.Radio className={defaultStyles['radio-description']} value={CostNegotiationMode.NET_COST}>
                        <span>Usar custo líquido</span>
                      </InputBox.Radio>
                      <InputBox.Radio className={defaultStyles['radio-description']} value={CostNegotiationMode.ENTRY_COST}>
                        <span>Usar custo de entrada</span>
                      </InputBox.Radio>
                    </InputBox.RadioGroup>
                    <InputBox.Error message={error?.message} />
                  </InputBox>
                )}
              />
            </Option.Content>
          </Option.Root>

          {selectedCostNegotiation === CostNegotiationMode.ENTRY_COST ? (
            <Callout variant="info" size="sm" className={styles.entryCostCallout}>
              <Callout.Title>Atenção</Callout.Title>
              <Callout.Description>{ENTRY_COST_WARNING}</Callout.Description>
              <a className={styles.contactLink} href="mailto:atendimento@infoprice.co">
                Entre em contato para mais informações
              </a>
            </Callout>
          ) : null}
        </Section.Content>
      </Section.Root>

      <Section.Root>
        <Section.Content>
          <Callout size="sm">
            As modificações realizadas até as 22h serão aplicadas na próxima integração de dados. Após esse horário, elas refletem na integração do dia
            seguinte.
          </Callout>
        </Section.Content>
      </Section.Root>

      <Footer>
        <Action theme="ghost" type="reset">
          Cancelar
        </Action>
        <Action type="submit">Salvar</Action>
      </Footer>
    </Layout.Root>
  );
});

SetupSupplierNegotiationConfig.displayName = 'SetupSupplierNegotiationConfig';

export { SetupSupplierNegotiationConfig };
